import type { GameId } from '@urbanrats/contracts';
import type { OpsCamera } from '@urbanrats/ops-core';
import { DataTable } from '../components/DataTable.tsx';
import { Link } from '../components/Link.tsx';
import { NotMeasured, PageHead, Section, StateTag } from '../components/common.tsx';
import { PauseControl } from '../components/PauseControl.tsx';
import { fmtTime, hrefWith, useCtx } from '../lib.ts';

const num = (v: number | null, unit = '') => (v === null ? <NotMeasured /> : `${v}${unit}`);

export function Cameras() {
  const { data, filters } = useCtx();
  const rows = data.cameras.filter((c) => !filters.camera || c.cameraAlias === filters.camera).filter((c) => !filters.game || c.games.includes(filters.game as GameId));
  return (
    <>
      <PageHead title="Cameras and sources">Values that were never measured show “not measured” and are never treated as healthy. Registry status comes from licence/readiness evidence.</PageHead>
      <DataTable<OpsCamera>
        testId="cameras-table"
        rows={rows}
        rowKey={(c) => c.sourceId}
        empty="No cameras match these filters."
        exportName="cameras"
        columns={[
          { key: 'name', header: 'Source', cell: (c) => <Link to={hrefWith(`/cameras/${c.sourceId}`, filters)}>{c.displayName}</Link>, csv: (c) => c.displayName },
          { key: 'status', header: 'Status', cell: (c) => <StateTag state={c.status} />, csv: (c) => c.status },
          { key: 'mode', header: 'Mode', cell: (c) => c.sourceMode, csv: (c) => c.sourceMode },
          { key: 'fps', header: 'FPS (measured)', cell: (c) => num(c.measuredFps), csv: (c) => c.measuredFps ?? 'not measured' },
          { key: 'age', header: 'Source age', cell: (c) => num(c.sourceAgeMs, ' ms'), csv: (c) => c.sourceAgeMs ?? 'not measured' },
          { key: 'clock', header: 'Clock trust', cell: (c) => c.clockTrust, csv: (c) => c.clockTrust },
          { key: 'lag', header: 'CV lag', cell: (c) => num(c.cvLagMs, ' ms'), csv: (c) => c.cvLagMs ?? 'not measured' },
          { key: 'rights', header: 'Rights expiry', cell: (c) => (c.rightsExpiresAt ? fmtTime(c.rightsExpiresAt) : 'none recorded'), csv: (c) => c.rightsExpiresAt ?? '' },
        ]}
      />
    </>
  );
}

export function CameraDetail({ id }: { id: string }) {
  const { data } = useCtx();
  const c = data.cameras.find((x) => x.sourceId === id);
  if (!c) return <p className="empty">Camera not found in your scope.</p>;
  return (
    <>
      <PageHead title={c.displayName}>
        {c.owner} · {c.mediaType} · licence {c.licence}
      </PageHead>
      <dl className="kv" data-testid="camera-detail">
        <dt>Status</dt>
        <dd>
          <StateTag state={c.status} /> {c.statusReason}
        </dd>
        <dt>Readiness</dt>
        <dd>{c.readiness}</dd>
        <dt>Source mode</dt>
        <dd>{c.sourceMode}</dd>
        <dt>FPS expected / measured</dt>
        <dd>
          {c.expectedFps ?? '—'} / {num(c.measuredFps)}
        </dd>
        <dt>Source age</dt>
        <dd>{num(c.sourceAgeMs, ' ms')}</dd>
        <dt>Capture clock trust</dt>
        <dd>{c.clockTrust}</dd>
        <dt>CV lag</dt>
        <dd>{num(c.cvLagMs, ' ms')}</dd>
        <dt>Missing frames (last minute)</dt>
        <dd>{num(c.missingFrames)}</dd>
        <dt>Offline / quarantine</dt>
        <dd>{c.status === 'QUARANTINED' ? 'quarantined' : c.status === 'OFFLINE' ? 'offline' : 'no'}</dd>
        <dt>Rights expiry</dt>
        <dd>{c.rightsExpiresAt ? fmtTime(c.rightsExpiresAt) : 'none recorded'}</dd>
      </dl>
      <Section title="Games on this source" testId="camera-games">
        {c.games.length === 0 ? <p className="muted">No game uses this source.</p> : c.games.map((g) => <PauseControl key={g} gameId={g} />)}
      </Section>
    </>
  );
}
